// The onboarding and manual-product forms' client-side checks — the same
// rules the API applies (portal-onboarding-drops.md), so most mistakes are
// caught before the upload. The API still decides: its answer is mapped back
// onto the field it names (fieldOfError). Pure, no React.
import { normalizeDomain } from '../embed/embedCode';
import { BRAND_IMAGE_MAX_BYTES, BRAND_IMAGE_TYPES, PRODUCT_IMAGES_MAX } from '../../config/onboarding';

export const BRAND_NAME_MAX = 100;
export const PRODUCT_NAME_MAX = 255;
export const PRODUCT_DESCRIPTION_MAX = 5000;
export const PRODUCT_SIZES_MAX = 30;
export const PRODUCT_SIZE_MAX = 32;

// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;

/// The name as the API stores it: trimmed, inner whitespace collapsed.
export function cleanName(raw) {
  return String(raw || '').replace(/\s+/g, ' ').trim();
}

/// null when the file may be uploaded, otherwise 'type' | 'size'.
export function imageProblem(file, { maxBytes = BRAND_IMAGE_MAX_BYTES } = {}) {
  if (!file) return null;
  if (!BRAND_IMAGE_TYPES.includes(String(file.type || '').toLowerCase())) return 'type';
  if (file.size > maxBytes) return 'size';
  return null;
}

/// The onboarding form → { errors: { field: code }, value } — `value` is what
/// createBrand sends (the cleaned name, the normalised domain).
export function validateBrandForm({ name, logo, cover, domain, terms }) {
  const errors = {};
  const cleaned = cleanName(name);
  if (!cleaned) errors.name = 'required';
  else if (cleaned.length > BRAND_NAME_MAX) errors.name = 'tooLong';
  else if (CONTROL.test(String(name))) errors.name = 'invalid';

  if (!logo) errors.logo = 'required';
  else if (imageProblem(logo)) errors.logo = imageProblem(logo);
  if (!cover) errors.cover = 'required';
  else if (imageProblem(cover)) errors.cover = imageProblem(cover);

  const d = normalizeDomain(domain);
  if (d.error) errors.domain = d.error;

  if (!terms) errors.terms = 'required';

  return { errors, value: { name: cleaned, websiteDomain: d.domain || null } };
}

/// POST /brands error → the form field it belongs to (null: a page-level
/// message). The API names the field; the code decides when it does not.
const BRAND_FIELDS = { name: 'name', websiteDomain: 'domain', termsAccepted: 'terms', termsVersion: 'terms', logo: 'logo', cover: 'cover' };
const BRAND_CODE_FIELDS = {
  INVALID_NAME: 'name',
  INVALID_DOMAIN: 'domain',
  TERMS_NOT_ACCEPTED: 'terms',
  INVALID_TERMS_VERSION: 'terms',
  LOGO_REQUIRED: 'logo',
  COVER_REQUIRED: 'cover',
};
export function brandErrorField({ code, field }) {
  if (field && BRAND_FIELDS[field]) return BRAND_FIELDS[field];
  return BRAND_CODE_FIELDS[code] || null;
}

// ── Manual product ──────────────────────────────────────────────────────────

/// "49990" / "49.90" / "49,90" → '49.90'-style string, or null.
export function normalizePrice(raw) {
  const s = String(raw ?? '').replace(/\s/g, '');
  if (!/^\d+(?:[.,]\d{1,2})?$/.test(s)) return null;
  const v = s.replace(',', '.');
  return Number(v) > 0 ? v : null;
}

/// "S, M; L  XL" → ['S', 'M', 'L', 'XL'] (case kept, duplicates dropped).
export function parseSizes(raw) {
  const seen = new Set();
  const out = [];
  String(raw || '')
    .split(/[,;\n]+/)
    .map((x) => x.trim())
    .filter(Boolean)
    .forEach((x) => {
      const key = x.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(x);
    });
  return out;
}

/// `images` = every photo the product will have (files, or kept photo URLs
/// when editing — only files are checked for type and size).
export function validateProductForm({ images, name, price, currency, sizes, description }) {
  const errors = {};
  const list = images || [];
  if (list.length === 0) errors.images = 'required';
  else if (list.length > PRODUCT_IMAGES_MAX) errors.images = 'tooMany';
  else {
    const bad = list.findIndex((f) => typeof f === 'object' && f && imageProblem(f));
    if (bad !== -1) errors.images = { code: imageProblem(list[bad]), index: bad };
  }
  const n = String(name || '').trim();
  if (!n) errors.name = 'required';
  else if (n.length > PRODUCT_NAME_MAX) errors.name = 'tooLong';

  const p = normalizePrice(price);
  if (!p) errors.price = String(price ?? '').trim() ? 'invalid' : 'required';
  const c = String(currency || '').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(c)) errors.currency = 'invalid';

  const s = Array.isArray(sizes) ? sizes : parseSizes(sizes);
  if (s.length > PRODUCT_SIZES_MAX) errors.sizes = 'tooMany';
  else if (s.some((x) => x.length > PRODUCT_SIZE_MAX)) errors.sizes = 'tooLong';

  const desc = String(description || '').trim();
  if (desc.length > PRODUCT_DESCRIPTION_MAX) errors.description = 'tooLong';

  return {
    errors,
    value: { images: list, name: n, price: p, currency: c, sizes: s, description: desc },
  };
}

const PRODUCT_CODE_FIELDS = {
  INVALID_NAME: 'name',
  INVALID_PRICE: 'price',
  INVALID_CURRENCY: 'currency',
  INVALID_SIZES: 'sizes',
  INVALID_DESCRIPTION: 'description',
  IMAGES_REQUIRED: 'images',
  TOO_MANY_FILES: 'images',
  INVALID_REMOVE_IMAGES: 'images',
  UNSUPPORTED_IMAGE_TYPE: 'images',
  IMAGE_TOO_LARGE: 'images',
  IMAGE_CORRUPT: 'images',
  UNEXPECTED_FILE_FIELD: 'images',
};
const PRODUCT_FIELDS = new Set(['images', 'name', 'price', 'currency', 'sizes', 'description']);
export function productErrorField({ code, field }) {
  if (field === 'removeImages') return 'images';
  if (field && PRODUCT_FIELDS.has(field)) return field;
  return PRODUCT_CODE_FIELDS[code] || null;
}
